import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntityFields, useCreateEntityField } from "@/pages/settings/hooks";
import { APIFieldItem, FieldDataSource } from "@/pages/settings/types";
import {
  formatFieldOptions,
  validateLinkedFieldForm,
  createLinkedFieldData,
  generateLinkedFieldLabel,
  shouldLoadFields,
  FieldOption
} from "../utils/linkedFieldUtils";
import { FORM_DEFAULTS } from "../const";

export interface UseLinkedFieldFormProps {
  onSave: (fieldData: any) => void;
  entityType?: EntityType;
}

export interface UseLinkedFieldFormReturn {
  formRef: React.RefObject<HTMLFormElement>;
  isFormValid: boolean;
  formData: any;
  formSetValue: any;
  setFormSetValue: (formMethods: { setValue: any; getValues: any }) => void;
  isSubmitting: boolean;
  selectedObjectType: EntityType | null;
  selectedFieldId: string | null;
  availableFields: APIFieldItem[];
  fieldOptions: FieldOption[];
  existingFieldLabels: string[];
  isLoading: boolean;
  handleFormValid: (valid: boolean, data: any) => void;
  handleFormSubmit: () => Promise<void>;
  handleSubmitButton: () => void;
}

export const useLinkedFieldForm = ({
  onSave,
  entityType = EntityType.DEAL
}: UseLinkedFieldFormProps): UseLinkedFieldFormReturn => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(
    FORM_DEFAULTS.INITIAL_FORM_VALID
  );
  const [formData, setFormData] = useState<any>(
    FORM_DEFAULTS.INITIAL_FORM_DATA
  );
  const [formSetValue, setFormSetValue] = useState<any>(null);

  const handleSetFormValue = (formMethods: {
    setValue: any;
    getValues: any;
  }) => {
    setFormSetValue(() => formMethods.setValue);
  };
  const [isSubmitting, setIsSubmitting] = useState<boolean>(
    FORM_DEFAULTS.INITIAL_SUBMITTING
  );

  const createFieldMutation = useCreateEntityField(entityType);

  const selectedObjectType = formData?.objectType as EntityType | null;
  const selectedFieldId = formData?.fieldId as string | null;

  const [currentEntityType, setCurrentEntityType] = useState<EntityType | null>(
    null
  );

  useEffect(() => {
    if (selectedObjectType) {
      setCurrentEntityType(selectedObjectType);
    }
  }, [selectedObjectType]);

  const entityTypeForAPI = currentEntityType || EntityType.DEAL;
  const { data: apiResponse, isLoading } = useEntityFields(entityTypeForAPI);

  const { data: currentEntityResponse } = useEntityFields(entityType);
  const existingFieldLabels = useMemo(
    () =>
      (currentEntityResponse?.fields || [])
        .map((field) => field.label?.trim().toLowerCase())
        .filter((label): label is string => !!label),
    [currentEntityResponse?.fields]
  );

  const availableFields = useMemo(() => {
    if (!shouldLoadFields(selectedObjectType, currentEntityType)) {
      return [];
    }

    const allFields = apiResponse?.fields || [];

    if (selectedObjectType === EntityType.DEAL) {
      return allFields.filter(
        (field) => field.dataSource === FieldDataSource.DEAL
      );
    }

    return allFields;
  }, [selectedObjectType, currentEntityType, apiResponse?.fields]);

  const fieldOptions = useMemo(() => {
    return formatFieldOptions(availableFields);
  }, [availableFields]);

  const handleFormValid = useCallback((valid: boolean, data: any) => {
    const isValid = validateLinkedFieldForm({ valid, ...data });
    setIsFormValid(isValid);
    setFormData(data);
  }, []);

  const handleFormSubmit = useCallback(async () => {
    const selectedField = availableFields.find(
      (f) => String(f.id) === String(selectedFieldId)
    );
    if (!selectedField || !selectedObjectType) return;

    setIsSubmitting(true);

    try {
      const label =
        formData?.fieldLabel?.trim() ||
        generateLinkedFieldLabel(selectedObjectType, selectedField);
      const createFieldData = createLinkedFieldData(
        selectedField,
        selectedObjectType,
        label
      );
      const result = await createFieldMutation.mutateAsync(createFieldData);
      onSave(result);
    } catch (error) {
      console.error("Failed to create linked field:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    availableFields,
    selectedFieldId,
    selectedObjectType,
    formData?.fieldLabel,
    createFieldMutation,
    onSave
  ]);

  const handleSubmitButton = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  return {
    formRef,
    isFormValid,
    formData,
    formSetValue,
    setFormSetValue: handleSetFormValue,
    isSubmitting,
    selectedObjectType,
    selectedFieldId,
    availableFields,
    fieldOptions,
    existingFieldLabels,
    isLoading,
    handleFormValid,
    handleFormSubmit,
    handleSubmitButton
  };
};
